import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { renderAsync } from 'docx-preview';
import { VIBE_CHAT_API_BASE } from '@/lib/vibeChatConfig';
import { formatApiError } from '@/lib/vibeChatApi';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Download, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Fetches and renders PRD .docx for one session (GET /chat/{sessionId}/prd).
 */
export default function PrdDocumentPreview({ sessionId, active, className }) {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [blob, setBlob] = useState(null);

  const prdUrl =
    VIBE_CHAT_API_BASE && sessionId
      ? `${VIBE_CHAT_API_BASE.replace(/\/$/, '')}/chat/${sessionId}/prd`
      : null;

  useEffect(() => {
    if (!active || !prdUrl || !sessionId) {
      setBlob(null);
      setError(null);
      setLoading(false);
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
      return undefined;
    }

    let cancelled = false;
    let retryTimer = null;
    let rendered = false;
    const loadForSession = sessionId;

    const load = async (isRetry = false) => {
      if (!isRetry) {
        setLoading(true);
        setError(null);
        setBlob(null);
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
        }
      }
      try {
        const { data, status } = await axios.get(prdUrl, {
          responseType: 'arraybuffer',
          headers: {
            Accept: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          },
          timeout: 120000,
          validateStatus: (s) => s === 200 || s === 404,
        });
        if (cancelled || loadForSession !== sessionId) return;
        if (status === 404) {
          setError(null);
          if (!isRetry) {
            setLoading(true);
          }
          return;
        }
        // Stop polling immediately. If a concurrent load() already rendered,
        // skip re-render to avoid clearing the container and resetting scroll.
        if (retryTimer) {
          clearInterval(retryTimer);
          retryTimer = null;
        }
        if (rendered) return;
        rendered = true;
        setBlob(data);
        setError(null);
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
          await renderAsync(data, containerRef.current, null, {
            className: 'docx-preview-surface',
            inWrapper: true,
            ignoreWidth: false,
            ignoreHeight: false,
          });
        }
        if (cancelled || loadForSession !== sessionId) {
          if (containerRef.current) {
            containerRef.current.innerHTML = '';
          }
          return;
        }
        setLoading(false);
      } catch (err) {
        if (!cancelled && loadForSession === sessionId) {
          setError(formatApiError(err));
          setLoading(false);
        }
      }
    };

    void load(false);
    retryTimer = setInterval(() => {
      if (!cancelled) {
        void load(true);
      }
    }, 4000);

    return () => {
      cancelled = true;
      if (retryTimer) {
        clearInterval(retryTimer);
      }
    };
  }, [active, prdUrl, sessionId]);

  const download = () => {
    if (!blob || !sessionId) return;
    const url = URL.createObjectURL(
      new Blob([blob], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      })
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = `requirements-prd-${sessionId.slice(0, 8)}.docx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!blob || loading}
          onClick={download}
          className="border-amber-200 bg-white"
        >
          <Download className="mr-2 h-4 w-4" />
          Download .docx
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="text-xs">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="relative min-h-[240px] overflow-auto rounded-xl border border-amber-200/80 bg-white p-4 shadow-inner docx-preview-host">
        {loading && (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-amber-800">
            <Loader2 className="h-8 w-8 animate-spin" />
            <span className="text-xs">Loading PRD…</span>
          </div>
        )}
        <div ref={containerRef} className={cn(loading && 'hidden')} />
      </div>
    </div>
  );
}